'use client';

/**
 * ContactSummaryCard — post-submit "Details shared" receipt (Decision #18.1,
 * 2026-07-hubspot-native-handoff.md).
 *
 * Renders as a compact, read-only transcript item in the exact spot the
 * pre-handoff contact form's card occupied — right before the HandoffBanner
 * switches to its "Connecting you to live support…" copy (see
 * handoff-banner-text.ts). Deliberately a RECEIPT, not a form: no inputs,
 * nothing interactive.
 *
 * Re-implemented on the shared StandardCard shell (design re-sync) — the
 * bespoke peach-badge chrome this card used to own now lives in StandardCard,
 * so this file only supplies the receipt-specific content: the check tile,
 * the "DETAILS SHARED" eyebrow, a name/email title, and the "Support only"
 * lock meta.
 *
 * One component, two call sites: the live CONTACT_SUMMARY event
 * (useAssistantConnection.ts → insertContactSummary) and a restored SYSTEM
 * row whose metadata carries `component: "contact_summary"`
 * (restore-messages.ts). Both paths converge on the same `ContactSummary`
 * shape (src/types/chat.ts) before reaching this component.
 *
 * Motion: `animateIn` (true only for the LIVE insert — message-block.tsx
 * passes `!restored`) plays an expand-in that trails the form card's
 * collapse-exit by a beat (0.18s), so the submit reads as the form folding
 * into this receipt. Restored rows render statically — a reloaded transcript
 * must not replay entrance animations. Honors prefers-reduced-motion.
 */
import { Check, Lock } from 'lucide-react';
import { motion, useReducedMotion } from 'framer-motion';
import { V3_COLORS } from '@/lib/design-tokens';
import type { ContactSummary } from '@/types/chat';
import { StandardCard, StandardCardMeta } from './primitives/standard-card';

/** Literal per spec — the " · " separator between name and email in the
 *  title line. Doesn't recur anywhere else yet, so it stays local rather
 *  than joining design-tokens.ts (see that file's own "2+ places" rule). */
const SEPARATOR_COLOR = '#c8bba5';

interface ContactSummaryCardProps extends ContactSummary {
  /** Play the live expand-in (paired with ContactInfoCard's collapse-exit).
   *  False for restored transcript rows. Defaults false — static render is
   *  the safe fallback for any call site that doesn't know. */
  animateIn?: boolean;
}

export function ContactSummaryCard({
  name,
  email,
  phone,
  animateIn = false,
}: ContactSummaryCardProps) {
  const reducedMotion = useReducedMotion();
  const animate = animateIn && !reducedMotion;

  return (
    <motion.div
      initial={animate ? { opacity: 0, height: 0, scale: 0.97 } : false}
      animate={{ opacity: 1, height: 'auto', scale: 1 }}
      transition={{
        duration: 0.32,
        delay: 0.18,
        ease: [0.16, 1, 0.3, 1],
      }}
      className="overflow-hidden"
    >
      <StandardCard
        icon={
          <Check aria-hidden="true" className="size-[15px]" strokeWidth={2.5} />
        }
        eyebrow="DETAILS SHARED"
        title={
          // min-w-0 + truncate: the text column above already constrains
          // width (flex-1 min-w-0), so a long name/email pair ellipsizes
          // instead of wrapping or overflowing the receipt.
          <div className="min-w-0 truncate">
            <span style={{ color: V3_COLORS.ink }}>{name}</span>
            <span className="font-normal" style={{ color: SEPARATOR_COLOR }}>
              {' '}
              ·{' '}
            </span>
            <span className="font-normal" style={{ color: V3_COLORS.muted }}>
              {email}
            </span>
            {/* Phone is optional and the design mock happens to show a user who
             *  left it blank — that's an absent value, NOT a spec to hide it.
             *  This card is the receipt for what was actually handed to
             *  support, so a number the user did provide has to appear here or
             *  the receipt misrepresents what was shared. */}
            {phone ? (
              <>
                <span
                  className="font-normal"
                  style={{ color: SEPARATOR_COLOR }}
                >
                  {' '}
                  ·{' '}
                </span>
                <span
                  className="font-normal"
                  style={{ color: V3_COLORS.muted }}
                >
                  {phone}
                </span>
              </>
            ) : null}
          </div>
        }
        aside={
          <StandardCardMeta>
            <Lock aria-hidden="true" className="size-[11px]" />
            Support only
          </StandardCardMeta>
        }
      />
    </motion.div>
  );
}
